import { Suspense, lazy, useCallback, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { LABELS } from "@match/shared";
import { PairView } from "@/components/pair";
import { ConvertedAmount } from "@/components/session";
import { Auto, Button, Dash, ErrorNote, Field, inputClass } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { dateTime, lots, money, points, sentence, signClass } from "@/lib/format";
import { decimalInput } from "@/lib/units";
import { keys, useDebounced, useMirrorIsFake, useOpenRiskEvents, useRefreshDirectory } from "@/lib/queries";
import { EventCard } from "@/pages/Events";
import type { HedgeLeg, HedgePair, LegCommission, LevelsState, Preview, SessionDetail } from "@/lib/types";

/*
 * Operatività di una Fase: la coppia di conti, i parametri dell'operazione, il
 * piano, l'apertura, la coppia aperta e la chiusura. Una sola implementazione,
 * usata dalla pagina Operazione e dalla scheda Operazione del dettaglio.
 *
 * L'operatore scrive DENARO — «rischio 200» — e il backend ne ricava i punti.
 * Quello che si legge accanto ai campi non è mai il numero digitato: è il
 * rischio VERO dopo l'arrotondamento dei punti e dei lotti, e sono due numeri,
 * uno per gamba. 200 su 1,33 lotti fanno 150 punti, cioè 199,50: si mostra
 * 199,50, e sul broker un numero diverso ancora.
 */

/**
 * Il grafico si carica SOLO quando serve, cioe' su una coppia aperta: sono ~45 kB
 * gzip di libreria, e questa e' la pagina che si apre tutti i giorni anche
 * quando non c'e' niente a mercato.
 */
const LevelsChart = lazy(() => import("@/components/chart").then((m) => ({ default: m.LevelsChart })));

const OPEN_STATES = ["PENDING", "BOTH_OPEN", "ONE_LEG"];

export const openPairOf = (s: SessionDetail) => s.pairs.find((p) => OPEN_STATES.includes(p.state)) ?? null;

/** Gli eventi aperti di una fase. Niente riquadro quando non ce n'e' nessuno. */
function SessionEvents({ sessionId }: { sessionId: string }) {
  const q = useOpenRiskEvents({ sessionId });
  if (!q.data?.rows.length) return null;
  return (
    <div className="space-y-2">
      {q.data.rows.map((e) => (
        <EventCard key={e.id} e={e} compact />
      ))}
    </div>
  );
}

/**
 * Quando si addebita la commissione, detto solo quando e' UNA cosa sola.
 * Con l'addebito diviso non si scrive niente: il totale e' gia' li', e
 * «meta' e meta'» in una riga di frizione non aiuta nessuno.
 */
function chargeLabel(c: LegCommission): string {
  if (c.total.amount <= 0) return "";
  if (c.atClose.amount === 0) return " all'apertura";
  if (c.atOpen.amount === 0) return " alla chiusura";
  return "";
}

export function SessionTrade({ s, canWrite }: { s: SessionDetail; canWrite: boolean }) {
  const refresh = useRefreshDirectory();
  const isFake = useMirrorIsFake();
  const [direction, setDirection] = useState<"LONG" | "SHORT">("LONG");
  // I valori dell'ultima operazione sono i PREDEFINITI, non configurazione:
  // senza operazioni precedenti i campi restano vuoti (DECISIONE 18).
  const last = s.lastOperation;
  const [propLots, setPropLots] = useState(last ? String(Number(last.propLots)) : "");
  const [multiplier, setMultiplier] = useState(last ? String(Number(last.brokerMultiplier)) : "");
  const [slMoney, setSlMoney] = useState(last?.slMoney ? String(Number(last.slMoney)) : "");
  const [tpMoney, setTpMoney] = useState(last?.tpMoney ? String(Number(last.tpMoney)) : "");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  /** Secondo clic richiesto sull'operazione che può bruciare il conto. */
  const [burnConfirm, setBurnConfirm] = useState(false);

  const openPair = openPairOf(s);
  const propCur = s.propAccount.currency;
  // Lotto, moltiplicatore e stop loss servono al piano: senza, niente da mostrare.
  const op = { lots: decimalInput(propLots), mult: decimalInput(multiplier), sl: decimalInput(slMoney), tp: decimalInput(tpMoney) };
  const complete = !!op.lots && !!op.mult && !!op.sl;
  // Una preview per tasto premuto sarebbe una richiesta per tasto premuto.
  const debounced = useDebounced(complete && !openPair ? JSON.stringify(op) : null, 300);
  const preview = useQuery({
    // Sotto "sessions": ogni modifica a conti o fasi la ricalcola.
    queryKey: [...keys.sessions, "preview", s.id, direction, s.state, debounced, s.summary.executedOps, s.propAccount.isConnected, s.brokerAccount.isConnected],
    queryFn: () => {
      const o = JSON.parse(debounced!) as typeof op;
      return api.sessions.preview(s.id, { direction, lots: o.lots!, multiplier: o.mult!, slMoney: o.sl!, ...(o.tp ? { tpMoney: o.tp } : {}) });
    },
    enabled: !!debounced,
    placeholderData: (prev) => prev,
  });

  const run = (name: string, fn: () => Promise<unknown>) => async () => {
    setBusy(name);
    setError(null);
    setNotice(null);
    setWarnings([]);
    try {
      const r = await fn();
      if (r && typeof r === "object" && "warnings" in r) setWarnings((r as { warnings: string[] }).warnings);
      if (r && typeof r === "object" && "positions" in r) {
        const b = r as Awaited<ReturnType<typeof api.dev.mirrorBootstrap>>;
        setNotice(`Mirror finto: ${b.accounts} conti collegati, ${b.positions} posizioni rimesse a mercato.${b.excluded.map((x) => ` Escluso ${x.label} (${x.mt5Login}): ${x.reason}`).join("")}`);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      await refresh();
      setBusy(null);
    }
  };

  // Il motivo scritto per cui non si apre. Con una coppia a mercato lo si sa
  // già; altrimenti lo dice il backend, con la stessa regola della rotta.
  const blocker = openPair
    ? // Una coppia scoperta blocca per un motivo diverso, e il motivo va detto:
      // metterne un'altra sopra somma due rischi che nessuno sta guardando.
      s.levels?.[openPair.id]?.desync
      ? "Una coppia di questa fase ha le due gambe su livelli diversi: riallineala prima di aprirne un'altra"
      : "C'è già una coppia aperta in questa fase: chiudila per aprirne un'altra"
    : !complete
      ? "Scrivi lotto, moltiplicatore e stop loss: la fase non li tiene, li decidi a ogni operazione"
      : preview.error
        ? errorMessage(preview.error)
        : (preview.data?.openBlocker?.message ?? null);
  const canOpen = !openPair && complete && !!preview.data?.plan && !blocker;
  // Non blocca: la bruciatura è l'esito profittevole. Chiede solo un secondo clic.
  const burn = !openPair && !blocker ? (preview.data?.openWarning ?? null) : null;
  const armed = burn && burnConfirm;
  const stale = preview.isFetching || (complete && debounced !== JSON.stringify(op));

  const field = (label: string, value: string, set: (v: string) => void, opts: { suffix?: string; hint?: string } = {}) => (
    <Field label={label} hint={opts.hint}>
      {(id) => (
        <div className="relative">
          <input
            id={id}
            inputMode="decimal"
            value={value}
            onChange={(e) => {
              set(e.target.value);
              setBurnConfirm(false);
            }}
            className={`${inputClass} ${opts.suffix ? "pr-12" : ""}`}
          />
          {opts.suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{opts.suffix}</span>}
        </div>
      )}
    </Field>
  );

  return (
    <div className="space-y-5">
      <PairView prop={s.propAccount} broker={s.brokerAccount} canWrite={canWrite} compact />

      {!openPair && (
        <section className="space-y-3 rounded-card border border-line bg-surface p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-medium text-ink">Parametri dell'operazione</h2>
            <div role="radiogroup" aria-label="Direzione sul prop" className="inline-flex overflow-hidden rounded border border-line">
              {(["LONG", "SHORT"] as const).map((d) => (
                <button
                  key={d}
                  role="radio"
                  aria-checked={direction === d}
                  onClick={() => {
                    setDirection(d);
                    setBurnConfirm(false);
                  }}
                  className={`px-4 py-1.5 text-sm ${direction === d ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
                >
                  {d === "LONG" ? "Long sul prop" : "Short sul prop"}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {field("Lotto sul prop", propLots, setPropLots)}
            {field("Moltiplicatore", multiplier, setMultiplier)}
            {/* La valuta è quella del CONTO PROP, non un simbolo fisso. */}
            {field("Stop loss", slMoney, setSlMoney, { suffix: propCur })}
            {field("Take profit", tpMoney, setTpMoney, { suffix: propCur, hint: "Vuoto = nessun take profit." })}
          </div>

          <AtMarket s={s} preview={preview.data} stale={stale} />

          {last && (
            <p className="text-xs text-faint">
              Precompilati con l'operazione n. {last.seq}, l'ultima di questa fase. Si cambiano a ogni apertura.
            </p>
          )}
        </section>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {canWrite && !openPair && (
          <>
            <Button
              variant={armed ? "danger" : "primary"}
              disabled={!!busy || !canOpen}
              onClick={
                burn && !burnConfirm
                  ? () => setBurnConfirm(true)
                  : run("open", async () => {
                      setBurnConfirm(false);
                      return api.sessions.openPair(s.id, {
                        direction,
                        propLots: op.lots!,
                        brokerMultiplier: op.mult!,
                        slMoney: Number(op.sl),
                        ...(op.tp ? { tpMoney: Number(op.tp) } : {}),
                      });
                    })
              }
            >
              {busy === "open" ? "Apertura…" : armed ? "Conferma: apri l'operazione che può bruciare il conto" : "Apri operazione"}
            </Button>
            {armed && (
              <Button variant="ghost" onClick={() => setBurnConfirm(false)}>
                Annulla
              </Button>
            )}
          </>
        )}
        {blocker ? (
          <span role="status" className="text-sm text-warn">
            {sentence(blocker)}
          </span>
        ) : (
          !openPair && !preview.data && <span className="text-sm text-muted">Calcolo del piano…</span>
        )}
      </div>

      {/*
        * Gli eventi APERTI di questa fase, qui e non in una pagina da cercare.
        *
        * Sono la ragione per cui un'apertura viene rifiutata — livelli non
        * allineati, gamba scoperta, esecuzione fuori posto — e il posto in cui
        * leggerli e' quello in cui il rifiuto compare. Da qui si prendono anche
        * in carico: chiuderli altrove vorrebbe dire lasciare questa schermata.
        */}
      <SessionEvents sessionId={s.id} />

      {burn && (
        <div role="status" className="rounded-card border border-line bg-raised/50 px-4 py-3 text-sm text-ink">
          <p>{burn.message}.</p>
          <p className="mt-1 text-xs text-muted">
            Nel modello Match è l'esito atteso del ciclo: si apre normalmente, con un secondo clic di conferma.
          </p>
        </div>
      )}

      {openPair ? (
        <OpenPairCard s={s} pair={openPair} canWrite={canWrite} levels={s.levels?.[openPair.id] ?? null} />
      ) : (
        // Senza piano il motivo è già scritto accanto al pulsante.
        preview.data?.plan && (
          <>
            <PlanView s={s} preview={preview.data} />
            <BurnProjection s={s} preview={preview.data} op={op} onSuggest={setMultiplier} />
          </>
        )
      )}

      <ErrorNote>{error}</ErrorNote>
      {notice && <p className="text-sm text-muted">{notice}</p>}
      {warnings.map((w) => (
        <p key={w} className="text-sm text-warn">
          {sentence(w)}
        </p>
      ))}

      {isFake && canWrite && (
        <div className="flex flex-wrap items-center gap-2 rounded-card border border-dashed border-line px-4 py-3 text-sm text-muted">
          <span>Mirror finto: simula il mercato per vedere il P&L muoversi</span>
          {[-500, -100, 100, 500].map((p) => (
            <Button key={p} size="sm" disabled={!!busy} onClick={run("move", () => api.dev.movePrice(p))}>
              {p > 0 ? `+${p}` : p} pt
            </Button>
          ))}
          <Button size="sm" variant="ghost" disabled={!!busy} onClick={run("bootstrap", api.dev.mirrorBootstrap)}>
            {busy === "bootstrap" ? "Ripristino…" : "Ricollega i conti dal database"}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Che cosa va DAVVERO a mercato con quello che è scritto nei campi.
 *
 * I punti derivati, il moltiplicatore che resta dopo l'arrotondamento dei lotti,
 * e il rischio in denaro RICALCOLATO — due numeri, uno per gamba, ciascuno nella
 * valuta del suo conto. Il numero digitato non compare: 200 chiesti su 1,33
 * lotti sono 199,50 a mercato, e dire 200 sarebbe dire un numero che non esiste.
 */
function AtMarket({ s, preview, stale }: { s: SessionDetail; preview: Preview | undefined; stale: boolean }) {
  if (!preview?.plan || !preview.money) return null;
  const { plan, money: m } = preview;
  const chiesto = m.requested;
  const arrotondato = chiesto !== null && Math.abs(chiesto.sl - m.atMarket.sl.prop) >= 0.005;

  const riga = (titolo: string, prop: number, broker: number, verso: "perde" | "guadagna") => (
    <div className="min-w-0">
      <p className="text-xs text-faint">{titolo}</p>
      <p className="mt-0.5 text-sm">
        <span className="text-prop">Prop</span>{" "}
        <span className={verso === "perde" ? "text-loss" : "text-gain"}>{money(prop, m.propCurrency)}</span>
      </p>
      <p className="text-sm">
        <span className="text-broker">Broker</span>{" "}
        <span className={verso === "perde" ? "text-gain" : "text-loss"}>{money(broker, m.brokerCurrency)}</span>
      </p>
    </div>
  );

  return (
    <div className={`rounded border border-line bg-raised/40 p-3 transition-opacity ${stale ? "opacity-60" : ""}`} aria-live="polite">
      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
        <div className="min-w-0">
          <p className="text-xs text-faint">A mercato</p>
          <p className="mt-0.5 text-sm text-ink">
            {lots(plan.prop.lots)} contro {lots(plan.broker.lots)} lotti
          </p>
          <p className="text-sm text-muted">
            SL {points(plan.prop.slPoints)} · TP {plan.prop.tpPoints === 0 ? "nessuno" : points(plan.prop.tpPoints)}
          </p>
        </div>
        <div className="min-w-0">
          <p className="text-xs text-faint">Moltiplicatore effettivo</p>
          <p className="mt-0.5 text-sm text-ink">{plan.effectiveMultiplier.toFixed(4)}</p>
          <p className="text-sm text-muted">dopo l'arrotondamento al lot step</p>
        </div>
        {riga("Se il prop va a stop loss", m.atMarket.sl.prop, m.atMarket.sl.broker, "perde")}
        {plan.prop.tpPoints > 0 && riga("Se il prop va a take profit", m.atMarket.tp.prop, m.atMarket.tp.broker, "guadagna")}
      </div>
      {arrotondato && chiesto && (
        <p className="mt-2 text-xs text-faint">
          Stop loss chiesto {money(chiesto.sl, chiesto.currency)}: i punti vanno per difetto, quindi a mercato il rischio è{" "}
          {money(m.atMarket.sl.prop, m.propCurrency)}.
        </p>
      )}
      {/* Lo stopsLevel si legge PRIMA di premere: se è letto dal broker blocca, se è scritto a mano avvisa. */}
      {preview.stopsLevel.map((v) => (
        <p key={`${v.role}-${v.level}`} className={`mt-2 text-xs ${v.live ? "text-loss" : "text-warn"}`} role="status">
          {v.level} della gamba {v.role === "PROP" ? "prop" : "broker"} a {points(v.points)}: {v.brokerSymbol} ne richiede almeno {points(v.stopsLevel)}.{" "}
          {v.live ? "Il server rifiuterebbe l'ordine." : "Valore scritto a mano nella pagina Server: controllalo."}
        </p>
      ))}
    </div>
  );
}

/**
 * Proiezione della bruciatura e calcolo inverso, ETICHETTATI.
 *
 * `projectBurn` assume lotto e moltiplicatore COSTANTI fino a esaurire il DD.
 * Finché stavano sulla Fase l'assunzione era vera per costruzione; ora che si
 * scelgono a ogni operazione non lo è più, e il numero non può comparire da
 * solo. Un numero che promette 678 senza dire da cosa dipende è peggio di
 * nessun numero.
 */
function BurnProjection({
  s,
  preview,
  op,
  onSuggest,
}: {
  s: SessionDetail;
  preview: Preview;
  op: { lots: string | null; mult: string | null; sl: string | null; tp: string | null };
  onSuggest: (v: string) => void;
}) {
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [proposta, setProposta] = useState<{ multiplier: number; nota: string } | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const projection = preview.projection;
  const propCur = s.propAccount.currency;

  const calcola = async () => {
    setErrore(null);
    setProposta(null);
    const netto = decimalInput(target);
    if (!netto || !op.lots || !op.mult || !op.sl) return setErrore("Scrivi il netto che vuoi, e compila i campi dell'operazione");
    setBusy(true);
    try {
      const r = await api.sessions.preview(s.id, {
        direction: "LONG",
        lots: op.lots,
        multiplier: op.mult,
        slMoney: op.sl,
        ...(op.tp ? { tpMoney: op.tp } : {}),
        targetNetGain: netto,
      });
      const inv = r.inverse;
      if (r.inverseMultiplier === null) {
        if (inv && inv.limit === "MAX_LOT" && inv.netGain.amount !== null) {
          return setErrore(
            `Obiettivo fuori portata: col lotto broker massimo (${inv.brokerLots}) il netto arriva a ` +
              `${inv.netGain.fxAssumed ? "≈ " : ""}${money(inv.netGain.amount, inv.netGain.currency)} con moltiplicatore ${inv.multiplier}. ` +
              `Abbassa l'obiettivo o alza il lotto prop.`,
          );
        }
        return setErrore(inv?.fxMissing ? "Tasso broker -> prop mancante: il moltiplicatore non è calcolabile" : "Moltiplicatore non calcolabile con questi parametri");
      }
      const nota =
        inv?.brokerLots != null && inv.netGain.amount !== null
          ? `${inv.brokerLots} lotti broker a mercato, netto ${inv.netGain.fxAssumed ? "≈ " : ""}${money(inv.netGain.amount, inv.netGain.currency)}` +
            `${inv.frictionIncluded ? "" : " prima della frizione"}` +
            (inv.limit === "MIN_LOT" ? " (lotto minimo del broker: l'obiettivo è superato)" : " (il minimo che raggiunge l'obiettivo)")
          : "";
      setProposta({ multiplier: r.inverseMultiplier, nota });
    } catch (e) {
      setErrore(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="card p-4 text-sm">
        <p className="text-xs text-faint">Se continui con questi valori fino alla bruciatura</p>
        <p className="mt-1">
          Netto sul reale{" "}
          <span className="font-medium">
            <ConvertedAmount value={projection.netGainIfBurn} signed missingHint="Senza quotazioni la frizione non si conosce" />
          </span>
        </p>
        {!projection.frictionIncluded && projection.netGainBeforeFriction.amount !== null && (
          <p className="text-xs text-faint">prima della frizione {money(projection.netGainBeforeFriction.amount, projection.currency)}</p>
        )}
        <p>
          Servono <span className="text-ink">{projection.opsToBurnExact.toLocaleString("it-IT")}</span> operazioni come questa ·{" "}
          frizione <ConvertedAmount value={projection.frictionIfBurn} />
        </p>
        <p className="mt-2 text-xs text-muted">
          È una proiezione condizionata: vale se ogni operazione da qui alla bruciatura ripete questo lotto e questo moltiplicatore. Cambiandoli, cambia.
        </p>
      </div>

      <div className="card p-4 text-sm">
        <p className="text-xs text-faint">Quale moltiplicatore per un netto voluto</p>
        <p className="mt-1 text-xs text-muted">Con il lotto prop scritto sopra, sempre nell'ipotesi che le operazioni si ripetano uguali.</p>
        <div className="mt-2 flex gap-2">
          <input
            aria-label="Netto voluto alla bruciatura"
            inputMode="decimal"
            value={target}
            placeholder={`Netto voluto in ${propCur}`}
            onChange={(e) => setTarget(e.target.value)}
            className={inputClass}
          />
          <Button disabled={busy} onClick={() => void calcola()}>
            {busy ? "Calcolo…" : "Calcola"}
          </Button>
        </div>
        {errore && <p className="mt-2 text-xs text-loss">{errore}</p>}
        {proposta && (
          // Suggerisce, non vincola: il campo resta dell'operatore.
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-ink">
              Moltiplicatore proposto <span className="font-medium tabular-nums">{proposta.multiplier}</span>
            </span>
            <Button size="sm" variant="ghost" onClick={() => onSuggest(String(proposta.multiplier))}>
              Usa questo
            </Button>
            {proposta.nota && <span className="w-full text-xs text-muted">{proposta.nota}</span>}
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * Le due gambe come andrebbero a mercato: prezzi, livelli, specifiche del
 * simbolo e frizione. Gli importi del rischio stanno sopra, in `AtMarket`, e la
 * proiezione sotto, in `BurnProjection`: qui non si ripetono.
 */
function PlanView({ s, preview }: { s: SessionDetail; preview: Preview }) {
  const plan = preview.plan!;
  const propCur = s.propAccount.currency;
  const brokerCur = s.brokerAccount.currency;
  const noQuotes = <Dash title="Quotazioni non disponibili" />;

  const legBox = (tone: "prop" | "broker", leg: typeof plan.prop, spec: typeof preview.specs.prop, cur: string) => (
    <section className={`p-4 ${tone === "prop" ? "pair-side-prop" : "pair-side-broker"}`}>
      <p className={`text-xs font-medium ${tone === "prop" ? "text-prop" : "text-broker"}`}>
        {tone === "prop" ? "Prop" : "Broker"} · {leg.brokerSymbol}
      </p>
      <p className="mt-1 text-2xl font-semibold text-ink">
        {leg.side === "BUY" ? "Acquisto" : "Vendita"} {lots(leg.lots)}
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <Stat label="Ingresso">{leg.entryPrice ?? noQuotes}</Stat>
        <Stat label="Rischio a stop loss">
          <span className={signClass(leg.lossAtSl)}>{money(leg.lossAtSl, cur)}</span>
        </Stat>
        {/* Zero punti = livello che non c'è: si scrive «nessuno», non un prezzo.
            Prima il motore ci metteva il prezzo di INGRESSO, e l'operatore
            leggeva uno stop loss che non sarebbe mai esistito. */}
        <Stat label={`Stop loss · ${leg.slPoints === 0 ? "—" : points(leg.slPoints)}`}>
          {leg.slPoints === 0 ? <span className="text-muted">nessuno</span> : (leg.slPrice ?? noQuotes)}
        </Stat>
        <Stat label={`Take profit · ${leg.tpPoints === 0 ? "—" : points(leg.tpPoints)}`}>
          {leg.tpPoints === 0 ? <span className="text-muted">nessuno</span> : (leg.tpPrice ?? noQuotes)}
        </Stat>
      </dl>
      <p className="mt-2 text-xs text-faint">
        valore punto {Number(spec.valuePerPoint)} {cur} · lot step {Number(spec.lotStep)}
      </p>
    </section>
  );

  const friction = plan.friction;
  return (
    <div className="space-y-4">
      <div className="pair">
        {legBox("prop", plan.prop, preview.specs.prop, propCur)}
        {legBox("broker", plan.broker, preview.specs.broker, brokerCur)}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="card p-4 text-sm">
          <p className="text-xs text-faint">Frizione sul reale, per operazione</p>
          <p className="mt-1 font-medium">{friction ? <ConvertedAmount value={friction.perOpOnReal} /> : "—"}</p>
          {friction && (
            <p className="mt-1 text-xs text-faint" title="Sul reale pesano i due spread sui lotti broker e la commissione del broker. La commissione del prop consuma DD, non denaro del cliente.">
              spread {friction.prop.spreadPoints} + {friction.broker.spreadPoints} pt = {money(friction.broker.spreadCost.amount, friction.broker.spreadCost.currency)} · commissione broker{" "}
              {money(friction.broker.commission.total.amount, friction.broker.commission.total.currency)}
              {chargeLabel(friction.broker.commission)}
              {friction.prop.commission.total.amount > 0 ? ` · prop ${money(friction.prop.commission.total.amount, friction.prop.commission.total.currency)} sul DD` : ""}
            </p>
          )}
          {/* La proiezione non si rifiuta di rispondere: dice il numero e dice
              su cosa. Stesso trattamento dei tassi assunti. */}
          {preview.commissionSource.prop === "UNKNOWN" && (
            <p className="mt-1 text-xs text-warn">
              Commissione del prop non ancora misurata: la proiezione la conta zero. La impara da sé alla prima operazione
              chiusa.
            </p>
          )}
          {preview.commissionSource.prop === "ESTIMATE" && (
            <p className="mt-1 text-xs text-faint">Commissione del prop stimata a mano: non ancora letta dai deal.</p>
          )}
        </div>
        <div className="card p-4 text-sm">
          <p className="text-xs text-faint">Budget di drawdown</p>
          <p className="mt-1">
            Operazioni residue prima di esaurirlo <span className="font-medium text-ink">{plan.remainingOps}</span>
          </p>
          <p>
            Oggi <span className="text-ink">{s.summary.opsToday}</span> di {s.maxOpsPerDay}
          </p>
        </div>
      </div>

      {preview.lotStepWarning && (
        <p className="rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn" role="status">
          {preview.lotStepWarning.message}
        </p>
      )}

      {!preview.marketAvailable && <p className="text-xs text-faint">Quotazioni non disponibili: connetti i due conti per vedere prezzi, livelli e frizione.</p>}
      {plan.warnings.map((w) => (
        <p key={w} className="text-sm text-warn">
          {sentence(w)}
        </p>
      ))}
    </div>
  );
}

function OpenPairCard({ s, pair, canWrite, levels }: { s: SessionDetail; pair: HedgePair; canWrite: boolean; levels: LevelsState | null }) {
  const legView = (leg: HedgeLeg | undefined, tone: "prop" | "broker") => {
    if (!leg) return <section className="p-4" />;
    const l = s.live[leg.id];
    return (
      <section className={`p-4 ${tone === "prop" ? "pair-side-prop" : "pair-side-broker"}`}>
        <p className={`text-xs font-medium ${tone === "prop" ? "text-prop" : "text-broker"}`}>
          {tone === "prop" ? "Prop" : "Broker"} · {leg.brokerSymbol} · ticket <span className="machine-id">{leg.mt5Ticket ?? "—"}</span>
        </p>
        <p className="mt-1 text-2xl font-semibold text-ink">
          {leg.side === "BUY" ? "Acquisto" : "Vendita"} {lots(leg.lots)}
        </p>
        <p className={`mt-1 text-lg ${l ? signClass(l.profit) : "text-muted"}`}>{l ? <Auto>{money(l.profit, leg.currency)}</Auto> : "P&L live non disponibile"}</p>
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <Stat label="Ingresso">{leg.openPrice ?? "—"}</Stat>
          <Stat label="Prezzo">{l?.price ?? "—"}</Stat>
          <Stat label="Stop loss">{leg.slPrice ?? "—"}</Stat>
          <Stat label="Punti allo SL">{l?.slPointsLeft === null || !l ? "—" : points(l.slPointsLeft)}</Stat>
          <Stat label="Take profit">{leg.tpPrice ?? "—"}</Stat>
          <Stat label="Punti al TP">{l?.tpPointsLeft === null || !l ? "—" : points(l.tpPointsLeft)}</Stat>
        </dl>
        {leg.orderReason && leg.orderReason !== "CLIENT" && <p className="mt-2 text-xs text-loss">Origine ordine {leg.orderReason}: atteso CLIENT</p>}
      </section>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-medium text-ink">
          Coppia #{pair.seq} · {pair.propDirection === "LONG" ? "Long" : "Short"} sul prop · {LABELS.pairState[pair.state]} · aperta {dateTime(pair.openedAt)}
        </h2>
        {canWrite && <ClosePairButton sessionId={s.id} pair={pair} />}
      </div>
      <div className="pair">
        {legView(pair.legs.find((l) => l.accountRole === "PROP"), "prop")}
        {legView(pair.legs.find((l) => l.accountRole === "BROKER"), "broker")}
      </div>
      {canWrite && <ModifyLevels s={s} pair={pair} levels={levels} />}
    </div>
  );
}

/**
 * Modifica dei livelli su una coppia a mercato.
 *
 * Si scrive DENARO, come in apertura, e nella valuta del conto prop: e' la
 * valuta in cui e' definito il valore punto, quindi la divisione torna senza
 * passare da un cambio. I prezzi li ricalcola il backend dal prezzo di apertura
 * EFFETTIVO di ciascuna gamba — non dalla quotazione corrente, o il rischio
 * promesso scivolerebbe col mercato.
 */
function ModifyLevels({ s, pair, levels }: { s: SessionDetail; pair: HedgePair; levels: LevelsState | null }) {
  const refresh = useRefreshDirectory();
  const propCur = s.propAccount.currency;
  // I predefiniti sono quelli CHIESTI sull'ultima modifica, che la coppia registra.
  const [sl, setSl] = useState(pair.slMoney ? String(Number(pair.slMoney)) : "");
  const [tp, setTp] = useState(pair.tpMoney ? String(Number(pair.tpMoney)) : "");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [esito, setEsito] = useState<string | null>(null);

  const d = levels?.desync ?? null;
  const manca = d ? (d.survivingLeg === "PROP" ? "broker" : "prop") : null;

  const invia = (nome: string, body: { slMoney: number; tpMoney?: number }) => async () => {
    setBusy(nome);
    setError(null);
    setEsito(null);
    try {
      const r = await api.sessions.modifyLevels(s.id, pair.id, body);
      setEsito(
        r.changed
          ? `Livelli a mercato: rischio ${money(r.money.atMarket.sl.prop, r.money.propCurrency)} sul prop, ` +
            `${money(r.money.atMarket.sl.broker, r.money.brokerCurrency)} sul broker.`
          : "I livelli erano gia' questi: nessuna chiamata al gateway.",
      );
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      await refresh();
      setBusy(null);
    }
  };

  const valori = { sl: decimalInput(sl), tp: decimalInput(tp) };
  const corpo = () => ({ slMoney: Number(valori.sl), ...(valori.tp ? { tpMoney: Number(valori.tp) } : {}) });

  /**
   * UNA lettura, all'apertura della schermata (vincolo del passo 11): non a ogni
   * render, non a ogni trascinamento. `PriceHistoryEx` puo' durare secondi e non
   * sta nel budget di un'operazione — ma non deve, perche' non blocca niente.
   * La chiave NON contiene i campi: trascinare non ricarica le candele.
   */
  const candles = useQuery({
    queryKey: [...keys.sessions, "candles", s.id, pair.id],
    queryFn: () => api.sessions.candles(s.id, pair.id),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const propLeg = pair.legs.find((l) => l.accountRole === "PROP");
  const propSymbol = s.symbols?.prop;
  // Il trascinamento SCRIVE nel campo: l'input resta la fonte della decisione.
  const onDrag = useCallback((level: "SL" | "TP", valore: number) => {
    const testo = String(valore);
    if (level === "SL") setSl(testo);
    else setTp(testo);
  }, []);

  return (
    <section className="space-y-3 rounded-card border border-line bg-surface p-4">
      {d && (
        <div role="alert" className="rounded border border-loss/40 bg-loss/10 px-3 py-2 text-sm text-loss">
          <p className="font-medium">
            Le due gambe hanno livelli diversi: scoperto per{" "}
            {d.amount === null ? "un livello che una gamba non ha" : money(d.amount, d.currency)} sulla gamba{" "}
            {d.survivingLeg === "PROP" ? "prop" : "broker"}
            {d.gapPoints === null ? "" : ` (${points(d.gapPoints)} di differenza sul ${d.level === "SL" ? "stop loss" : "take profit"})`}.
          </p>
          <p className="mt-1 text-xs">
            La modifica e' riuscita su una gamba sola. I livelli vecchi non sono stati rimessi: rimetterli passa dallo stesso
            trasporto che ha appena fallito, e sulla gamba riuscita chiuderebbe la posizione che stavi proteggendo. Finche'
            resta cosi', su questa fase non si apre niente.
          </p>
          <div className="mt-2">
            <Button
              size="sm"
              variant="danger"
              disabled={!!busy || !valori.sl}
              onClick={invia("riallinea", corpo())}
            >
              {busy === "riallinea" ? "Riallineamento…" : `Riallinea la gamba ${manca}`}
            </Button>
          </div>
        </div>
      )}
      {levels && !levels.desync && !levels.matchesRequest && (
        <p role="status" className="text-sm text-warn">
          Le due gambe si coprono, ma i livelli a mercato non sono quelli chiesti: l'ultima modifica non e' stata applicata.
        </p>
      )}

      <h3 className="text-sm font-medium text-ink">Modifica i livelli</h3>

      {/* Il grafico e' la LETTURA. Se non c'e', i campi funzionano uguale: e'
          lo stesso comportamento del trascinamento che non si puo' usare. */}
      {candles.isError && (
        <p className="text-xs text-faint">
          Grafico non disponibile: {errorMessage(candles.error)}. I livelli si scrivono lo stesso nei campi qui sotto.
        </p>
      )}
      {candles.data && propLeg?.openPrice && propSymbol && (
        <Suspense fallback={<p className="text-xs text-faint">Caricamento del grafico…</p>}>
          <LevelsChart
            candles={candles.data}
            leg={{
              side: propLeg.side,
              openPrice: Number(propLeg.openPrice),
              slPrice: propLeg.slPrice === null ? null : Number(propLeg.slPrice),
              tpPrice: propLeg.tpPrice === null ? null : Number(propLeg.tpPrice),
              lots: Number(propLeg.lots),
            }}
            point={Number(propSymbol.point)}
            valuePerPoint={Number(propSymbol.valuePerPoint)}
            currency={propCur}
            slMoney={sl}
            tpMoney={tp}
            onDrag={onDrag}
          />
        </Suspense>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Field label="Stop loss">
          {(id) => (
            <div className="relative">
              <input id={id} inputMode="decimal" value={sl} onChange={(e) => setSl(e.target.value)} className={`${inputClass} pr-12`} />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{propCur}</span>
            </div>
          )}
        </Field>
        <Field label="Take profit" hint="Vuoto = nessun take profit.">
          {(id) => (
            <div className="relative">
              <input id={id} inputMode="decimal" value={tp} onChange={(e) => setTp(e.target.value)} className={`${inputClass} pr-12`} />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{propCur}</span>
            </div>
          )}
        </Field>
        <div className="flex items-end">
          <Button disabled={!!busy || !valori.sl} onClick={invia("modifica", corpo())}>
            {busy === "modifica" ? "Modifica…" : "Applica"}
          </Button>
        </div>
      </div>
      <p className="text-xs text-faint">
        Le distanze si contano dal prezzo di apertura di ciascuna gamba, non dalla quotazione di adesso: il rischio che il
        gestionale promette e' la distanza dall'ingresso.
      </p>
      {esito && <p className="text-sm text-muted">{esito}</p>}
      <ErrorNote>{error}</ErrorNote>
    </section>
  );
}

/** Chiusura delle due gambe, con conferma. Usata dalla coppia aperta e dall'elenco operazioni. */
export function ClosePairButton({ sessionId, pair, size }: { sessionId: string; pair: HedgePair; size?: "sm" }) {
  const refresh = useRefreshDirectory();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.sessions.closePair(sessionId, pair.id);
      setConfirm(false);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      await refresh();
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {confirm ? (
        <>
          <Button size={size} variant="danger" disabled={busy} onClick={() => void close()}>
            {busy ? "Chiusura…" : `Conferma chiusura della coppia #${pair.seq}`}
          </Button>
          <Button size={size} variant="ghost" disabled={busy} onClick={() => setConfirm(false)}>
            Annulla
          </Button>
        </>
      ) : (
        <Button size={size} onClick={() => setConfirm(true)}>
          Chiudi coppia
        </Button>
      )}
      {error && (
        <span role="alert" className="text-xs text-loss">
          {error}
        </span>
      )}
    </span>
  );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}
